import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PUBLIC_ROUTES } from "@/routes/public";
import { getPublishedArticles } from "@/content/articles";
import { PublicContentLayout } from "@/components/public/PublicContentLayout";
import { formatContentDate } from "@/content/articles/dates";

/** JUB-123: list of published product guides. */
export default function BlogIndexPage() {
  const { t } = useTranslation();
  const locale = useRouteLocale();
  const articles = getPublishedArticles();

  usePageMeta({
    title: t("blog.index.metaTitle"),
    description: t("blog.index.metaDescription"),
    locale,
    path: PUBLIC_ROUTES.blog(locale),
    alternates: { ro: PUBLIC_ROUTES.blog("ro"), en: PUBLIC_ROUTES.blog("en") },
  });

  return (
    <PublicContentLayout>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">{t("blog.index.title")}</h1>
      <p className="mt-3 text-muted-foreground">{t("blog.index.subtitle")}</p>

      {articles.length === 0 ? (
        <p className="mt-10 text-muted-foreground">{t("blog.index.empty")}</p>
      ) : (
        <ul className="mt-10 space-y-6" data-testid="blog-article-list">
          {articles.map((article) => (
            <li key={article.slug} className="rounded-2xl border border-border bg-white p-6" data-testid="blog-article-card">
              <h2 className="text-xl font-semibold text-foreground">
                <Link to={PUBLIC_ROUTES.blogArticle(locale, article.slug)} className="hover:text-pink-600 transition-colors">
                  {article.title[locale]}
                </Link>
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                <time dateTime={article.updatedAt}>
                  {t("blog.updatedOn", { date: formatContentDate(article.updatedAt, locale) })}
                </time>
              </p>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">{article.summary[locale]}</p>
              <Link
                to={PUBLIC_ROUTES.blogArticle(locale, article.slug)}
                className="mt-4 inline-block text-sm font-medium text-pink-600 hover:text-pink-700"
                aria-label={`${t("blog.readMore")}: ${article.title[locale]}`}
              >
                {t("blog.readMore")}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PublicContentLayout>
  );
}
